"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../../lib/utils'
import { WidgetBase, WidgetBaseProps } from '../core/widget-base'
import { 
  TrendingUp, 
  TrendingDown, 
  ArrowUpRight, 
  ArrowDownRight,
  Minus,
  Target,
  Activity
} from 'lucide-react'
import { Badge } from '../../ui/badge'
import { Progress } from '../../ui/progress'
import { useSubscription } from '../../../hooks/use-subscription'
import { ProLockScreen } from '../../shared/pro-lock-screen'

export interface KPIData {
  label: string
  value: number | string
  unit?: string
  change?: {
    value: number
    type: 'increase' | 'decrease' | 'neutral'
    period?: string
  }
  target?: {
    value: number
    label?: string
  }
  status?: 'success' | 'warning' | 'danger' | 'info'
  trend?: number[]
  icon?: React.ReactNode
  description?: string
}

export interface KPIWidgetProps extends Omit<Partial<WidgetBaseProps>, 'variant'> {
  data: KPIData | KPIData[]
  title?: string
  variant?: 'default' | 'compact' | 'detailed' | 'card'
  backgroundVariant?: 'default' | 'minimal' | 'glass' | 'bordered' | 'gradient' | 'colored' | 'overlay' | 'floating'
  gradientDirection?: 'to-t' | 'to-tr' | 'to-r' | 'to-br' | 'to-b' | 'to-bl' | 'to-l' | 'to-tl'
  orientation?: 'horizontal' | 'vertical'
  showTrend?: boolean
  showTarget?: boolean
  showChange?: boolean
  animate?: boolean
  icon?: React.ReactNode
  
  onKPIClick?: (kpi: KPIData) => void
}

export function KPIWidget({
  data,
  title,
  variant = 'default',
  backgroundVariant,
  gradientDirection,
  orientation = 'horizontal',
  showTrend = true,
  showTarget = true,
  showChange = true,
  animate = true,
  icon,
  
  onKPIClick,
  ...widgetProps
}: KPIWidgetProps) {
  const { hasProAccess, isLoading } = useSubscription();

  const kpis = Array.isArray(data) ? data : [data]

  // Format number with max 2 decimal places
  const formatValue = (value: number | string, unit?: string): string => {
    if (typeof value === 'string') return value

    let formattedValue: string
    if (value % 1 !== 0) {
      // Has decimals, round to 2 decimal places max
      formattedValue = (Math.round(value * 100) / 100).toString()
    } else {
      formattedValue = value.toString()
    }

    return unit ? `${formattedValue}${unit}` : formattedValue
  }

  // Get status color
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'success': return 'text-green-600 dark:text-green-400'
      case 'warning': return 'text-yellow-600 dark:text-yellow-400'
      case 'danger': return 'text-red-600 dark:text-red-400'
      case 'info': return 'text-blue-600 dark:text-blue-400'
      default: return 'text-foreground'
    }
  }

  // Render trend sparkline
  const renderTrend = (trend: number[]) => {
    if (!trend || trend.length < 2) return null

    const max = Math.max(...trend)
    const min = Math.min(...trend)
    const range = max - min || 1

    return (
      <div className="flex items-end gap-0.5 h-8 w-16">
        {trend.map((value, index) => (
          <motion.div
            key={index}
            className="flex-1 bg-primary/60 rounded-t-sm min-h-[2px]"
            initial={{ height: 0 }}
            animate={{ height: `${Math.max(((value - min) / range) * 100, 10)}%` }}
            transition={{ delay: index * 0.05, duration: animate ? 0.3 : 0 }}
          />
        ))}
      </div>
    )
  }

  // Render single KPI
  const renderKPI = (kpi: KPIData, index: number) => {
    const progress = kpi.target ? (typeof kpi.value === 'number' ? (kpi.value / kpi.target.value) * 100 : 0) : 0

    return (
      <motion.div
        key={index}
        className={cn(
          "space-y-2",
          onKPIClick && "cursor-pointer hover:bg-muted/30 p-2 -m-2 rounded-lg transition-colors"
        )}
        onClick={() => onKPIClick?.(kpi)}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: animate ? 0.3 : 0 }}
      >
        {/* Default and Detailed variants */}
        {(variant === 'default' || variant === 'detailed') && (
          <div className={cn(
            "flex items-center",
            orientation === 'vertical' ? 'flex-col space-y-2' : 'justify-between'
          )}>
            <div className={cn(
              "flex items-center gap-2",
              orientation === 'vertical' ? 'flex-col text-center' : ''
            )}>
              {kpi.icon && (
                <div className="text-muted-foreground">
                  {kpi.icon}
                </div>
              )}
              <div className={orientation === 'vertical' ? 'text-center' : ''}>
                <p className="text-sm text-muted-foreground font-medium">{kpi.label}</p>
                {kpi.description && (
                  <p className="text-xs text-muted-foreground/70">{kpi.description}</p>
                )}
              </div>
            </div>

            <div className={cn(
              "flex items-center gap-3",
              orientation === 'vertical' ? 'flex-col' : ''
            )}>
              <div className={cn(
                "text-right",
                orientation === 'vertical' ? 'text-center' : ''
              )}>
                <p className={cn(
                  "text-2xl font-bold tracking-tight",
                  getStatusColor(kpi.status)
                )}>
                  {formatValue(kpi.value, kpi.unit)}
                </p>
              </div>

              {showChange && kpi.change && (
                <div className={cn(
                  "flex items-center gap-1",
                  kpi.change.type === 'increase' ? 'text-green-600' : 
                  kpi.change.type === 'decrease' ? 'text-red-600' : 
                  'text-gray-600'
                )}>
                  {kpi.change.type === 'increase' ? <TrendingUp className="h-4 w-4" /> : 
                   kpi.change.type === 'decrease' ? <TrendingDown className="h-4 w-4" /> : 
                   <Minus className="h-4 w-4" />}
                  <span className="text-sm font-medium">
                    {kpi.change.type !== 'neutral' && (kpi.change.type === 'increase' ? '+' : '-')}
                    {Math.abs(kpi.change.value)}%
                  </span>
                </div>
              )}

              {showTrend && kpi.trend && renderTrend(kpi.trend)}
            </div>
          </div>
        )}

        {/* Card variant */}
        {variant === 'card' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{kpi.label}</p>
                <p className="text-3xl font-bold tracking-tight">
                  {formatValue(kpi.value, kpi.unit)}
                </p>
              </div>
              {icon && (
                <div className="flex-shrink-0 p-3 rounded-xl bg-primary/10">
                  {icon}
                </div>
              )}
            </div>
            
            {showChange && kpi.change && (
              <div className="flex items-center gap-2">
                <Badge 
                  variant={kpi.change.type === 'increase' ? 'success' : 
                          kpi.change.type === 'decrease' ? 'destructive' : 'secondary'}
                  leftIcon={kpi.change.type === 'increase' ? <ArrowUpRight className="h-3 w-3" /> : 
                           kpi.change.type === 'decrease' ? <ArrowDownRight className="h-3 w-3" /> : 
                           <Minus className="h-3 w-3" />}
                  className="text-xs"
                >
                  {kpi.change.type !== 'neutral' && (kpi.change.type === 'increase' ? '+' : '-')}
                  {Math.abs(kpi.change.value)}%
                </Badge>
                {kpi.change.period && (
                  <span className="text-xs text-muted-foreground">
                    vs {kpi.change.period}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Compact variant */}
        {variant === 'compact' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {kpi.icon && (
                <div className="text-muted-foreground text-sm">
                  {kpi.icon}
                </div>
              )}
              <span className="text-sm font-medium">{kpi.label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold">{formatValue(kpi.value, kpi.unit)}</span>
              {showChange && kpi.change && (
                <Badge 
                  variant="outline" 
                  className="text-xs"
                  leftIcon={kpi.change.type === 'increase' ? <ArrowUpRight className="h-3 w-3" /> : 
                           kpi.change.type === 'decrease' ? <ArrowDownRight className="h-3 w-3" /> : 
                           <Minus className="h-3 w-3" />}
                >
                  {kpi.change.type !== 'neutral' && (kpi.change.type === 'increase' ? '+' : '-')}
                  {Math.abs(kpi.change.value)}%
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Target progress for detailed variant */}
        {variant === 'detailed' && showTarget && kpi.target && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {kpi.target.label || 'Target'}
              </span>
              <span className="font-medium">
                {progress.toFixed(1)}% of {formatValue(kpi.target.value, kpi.unit)}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
      </motion.div>
    )
  }

  // Pro access check - moved to the end after all hooks
  if (!isLoading && !hasProAccess) {
    return (
      <ProLockScreen 
        componentName="KPI Widget" 
        compact={true}
      />
    );
  }

  return (
    <WidgetBase
      title={title}
      variant={backgroundVariant}
      gradientDirection={gradientDirection}
      {...widgetProps}
    >
      <div className={cn(
        "space-y-4",
        variant === 'compact' && "space-y-2"
      )}>
        {kpis.map((kpi, index) => renderKPI(kpi, index))}
      </div>
    </WidgetBase>
  )
}

export default KPIWidget